import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type {
  DeviceDto,
  PauseSessionInput,
  SessionScreenOutcome,
  SessionScreenResult,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * Stopping and restarting the BILLING clock on one sitting — SPEC §5.3, the
 * door for `POST /sessions/:id/pause` and `.../resume`.
 *
 * The case it exists for: a paying customer goes out to eat. Their machine is
 * still theirs and the sitting is still open, but the venue has decided not to
 * charge for the empty chair. That is not the same act as ending the
 * reservation, which frees the machine and puts the time on the bill.
 *
 * **Never labelled "Pause" here.** On this board Play/Pause already mean
 * something else — swinging a PC between free-play and the portal login screen
 * — so the money version is spelled out: "Hold the clock" and "Continue". See
 * the note in `deviceMenu.ts`.
 *
 * Which half runs is read from `session.paused` rather than passed in, so a
 * poll that landed between opening the menu and confirming shows the truth
 * instead of firing a pause at an already-paused session (a 409).
 */

/**
 * Does this outcome have to be said out loud?
 *
 * Pause does two things that fail independently: the clock stops
 * unconditionally, the screen seal may not land at all. `offline` and
 * `unsupported` both mean THE CLOCK STOPPED AND THE CUSTOMER IS STILL PLAYING,
 * for free — the one thing staff cannot be allowed to discover later, and the
 * reason this dialog does not just close on a 200.
 *
 * `no_agent` is NOT a fault and must never be dressed as one: a pool table or
 * a cafe seat has no screen to seal, so there is nothing to report and nothing
 * for staff to go and check. Warning about it would train them to ignore the
 * warning that matters.
 */
export function screenOutcomeNeedsTelling(outcome: SessionScreenOutcome): boolean {
  return outcome === 'offline' || outcome === 'unsupported';
}

/**
 * The warning itself — presentational and stateless, so the four outcomes can
 * be pinned by static-markup tests the way `ActionsMenuPanel` is.
 *
 * `offline` and `unsupported` get DIFFERENT text on purpose. They have the
 * same consequence and completely different fixes: one machine is switched off
 * or off the network, the other is running an old kiosk build and will keep
 * failing however many times it is rebooted.
 */
export function ScreenOutcomeNotice({
  outcome,
  deviceName,
  action,
}: {
  outcome: SessionScreenOutcome;
  deviceName: string;
  action: 'hold' | 'continue';
}) {
  const { t } = useTranslation();
  if (!screenOutcomeNeedsTelling(outcome)) return null;

  return (
    <div
      role="alert"
      data-screen-outcome={outcome}
      className="mt-4 flex gap-2.5 rounded-card border border-ending/40 bg-ending/10 p-3"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-ending" aria-hidden />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-content">
          {t(`deviceBoard.holdDialog.screen.${action}Title`)}
        </p>
        <p className="mt-1 text-sm text-content-secondary">
          {t(`deviceBoard.holdDialog.screen.${outcome}.${action}`, { device: deviceName })}
        </p>
        <p className="mt-1 text-xs text-content-muted">
          {t(`deviceBoard.holdDialog.screen.${outcome}.fix`)}
        </p>
      </div>
    </div>
  );
}

export function HoldClockDialog({
  device,
  onClose,
  onDone,
}: {
  device: DeviceDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const session = device.currentSession;
  const action: 'hold' | 'continue' = session?.paused ? 'continue' : 'hold';

  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Set only once the call has succeeded. Non-null with a tellable outcome is
  // what keeps the dialog open on a screen that was not sealed.
  const [outcome, setOutcome] = useState<SessionScreenOutcome | null>(null);

  async function handleConfirm(): Promise<void> {
    if (!session) return;
    setError(null);
    setSubmitting(true);
    try {
      const result =
        action === 'hold'
          ? await api.post<SessionScreenResult>(`/sessions/${session.id}/pause`, {
              // Trimmed away rather than sent empty: the schema's `reason` is
              // optional and a blank note is noise in the activity report.
              ...(reason.trim() ? { reason: reason.trim() } : {}),
            } satisfies PauseSessionInput)
          : await api.post<SessionScreenResult>(`/sessions/${session.id}/resume`, {});

      // The board is refreshed either way — the clock moved whatever the screen
      // did, and the tile must show it. Only the dialog stays.
      onDone();
      if (screenOutcomeNeedsTelling(result.screen)) setOutcome(result.screen);
      else onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('sessions.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  const title = session
    ? t(`deviceBoard.holdDialog.${action}Title`, { device: device.name })
    : t('deviceBoard.holdDialog.nothingTitle', { device: device.name });

  const fieldClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none focus:border-primary';

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-sm rounded-card border border-subtle bg-raised p-5 shadow-2xl">
        <h2 className="text-lg font-semibold text-content">{title}</h2>

        {!session ? (
          // Menu raced the poll: the sitting ended while the menu was open.
          <p className="mt-2 text-sm text-content-muted">{t('deviceBoard.holdDialog.nothing')}</p>
        ) : outcome ? (
          <ScreenOutcomeNotice outcome={outcome} deviceName={device.name} action={action} />
        ) : (
          <>
            <p className="mt-2 text-sm text-content-secondary">
              {t(`deviceBoard.holdDialog.${action}Message`, { tab: session.tabName })}
            </p>

            {action === 'hold' ? (
              <label className="mt-4 block">
                <span className="mb-1 block text-xs text-content-secondary">
                  {t('deviceBoard.holdDialog.reason')}
                </span>
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  maxLength={200}
                  rows={2}
                  autoFocus
                  className={fieldClass}
                />
                {/* Staff's own note for the §5.7 activity report — it is not
                    forwarded to the customer's screen, and saying so stops
                    anyone writing a message TO the customer in it. */}
                <span className="mt-1 block text-xs text-content-muted">
                  {t('deviceBoard.holdDialog.reasonHint')}
                </span>
              </label>
            ) : null}
          </>
        )}

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          {outcome ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover"
            >
              {t('deviceBoard.holdDialog.screen.acknowledge')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
              >
                {t('common.cancel')}
              </button>
              {session ? (
                <button
                  type="button"
                  onClick={() => void handleConfirm()}
                  disabled={submitting}
                  className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
                >
                  {submitting ? t('common.loading') : t(`deviceBoard.holdDialog.${action}Confirm`)}
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
